import type { FilterUndefinedValues, FilterEmptyValues, ThrottleCallback, ThrottleOptions } from './types/global'
import type { AxiosPromise } from 'axios'
import { cloneDeep } from 'lodash-es'
import FileSaver from 'file-saver'

/**
 * 随机生成min~max之间的数
 * @param {number} min  最小值
 * @param {number} max 最大值
 * @returns
 */
function getRandomNum(min: number, max: number) {
  return Math.round(Math.random() * (max - min) + min)
}

// 随机生成字符串
function randomString(n: number) {
  n = n || 32
  const t = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz'
  const a = t.length
  let str = ''
  for (let i = 0; i < n; i++) {
    str += t.charAt(Math.floor(Math.random() * a))
  }

  return str
}

/** 深度克隆 */
const deepClone = <T extends Record<string, any>>(obj: T) => {
  return cloneDeep(obj)
}

/**
 * 数组去重
 */
function unique<T>(arr: T[]): T[] {
  return Array.from(new Set(arr))
}

// 阿拉伯数字转换成中文数字
function convertToChineseNumeral(num: number, curLanguage: string) {
  if (curLanguage === 'en') return num
  if (num == 10) {
    return '十'
  } else if (num == 1) {
    return '一'
  }
  const digits = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九']
  const units = ['', '十', '百', '千', '万']
  let result = ''
  const numStr = num.toString()
  for (let i = 0; i < numStr.length; i++) {
    const digit = parseInt(numStr.charAt(i))
    const unit = units[numStr.length - i - 1]
    if (digit === 0) {
      // 当前数字为0时不需要输出汉字，但需要考虑上一个数字是否为0，避免出现连续的零
      if (result.charAt(result.length - 1) !== '零') {
        result += '零'
      }
    } else {
      result += digits[digit] + unit
    }
  }
  // 对于一些特殊的数字，如10、100等，需要在最前面加上“一”
  if (result.charAt(0) === '一') {
    result = result.substr(1, result.length)
  } else if (result.charAt(0) === '百') {
    result = '一' + result
  } else if (result.charAt(0) === '千') {
    result = '一' + result
  }
  return result
}

// 通过url转为blob格式的数据
function urlToBlob(url: string) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('GET', url, true)
    xhr.responseType = 'blob'
    xhr.onload = function () {
      if (xhr.status == 200) {
        resolve(xhr.response)
      } else {
        reject()
      }
    }
    xhr.onerror = () => reject()
    xhr.send()
  })
}

// 利用file-saver保存url文件 自定义文件名
function downloadUrlFile(url: string, fileName = '') {
  urlToBlob(url).then((data: any) => {
    FileSaver.saveAs(data, fileName)
  })
}

// 通过文件名获取文件类型
function getFileType(name = '') {
  if (name == '') return 'other'
  if (/\.(avi|wmv|mpg|mov|rm|ram|flv|mp4|rmvb|mkv|m2v|m4v)/i.test(name)) return 'video'
  if (/\.(ppt|pptx)/i.test(name)) return 'ppt'
  if (/\.pdf/i.test(name)) return 'pdf'
  if (/\.(doc|docx)/i.test(name)) return 'docx'
  if (/\.(xls|xlsx)/i.test(name)) return 'xls'
  if (/\.(mp3|wav|wmv)/i.test(name)) return 'music'
  if (/\.(png|jpg|jpeg|bmp|gif|webp)/i.test(name)) return 'pic'
  if (/\.(zip|rar|7r)/i.test(name)) return 'zip'
  return 'other'
}

// 获取assets静态资源
function getAssetsFile(url: string) {
  return new URL(`../assets/imgs/${url}`, import.meta.url).href
}

// 判断对象是否为空
function isEmptyObject(obj: any) {
  return Object.keys(obj).length === 0
}

// 对象转url参数
function objToUrlParams(obj: any) {
  let str = ''
  for (const key in obj) {
    str += `${key}=${obj[key]}&`
  }
  return str.slice(0, -1)
}

/**
 * @description 过滤掉对象中值为undefined的属性
 * @param {T extends Record<string, any>} obj 传入的对象数据
 * @returns {Pick<T, FilterUndefinedValues<T>>} 返回过滤掉undefined值的对象
 */
function filterUndefinedValues<T extends Record<string, any>>(obj: T): Pick<T, FilterUndefinedValues<T>> {
  const result: Partial<T> = {}
  Object.entries(obj).forEach(([key, value]) => {
    if (value !== undefined) {
      result[key as keyof T] = value
    }
  })
  return result as Pick<T, FilterUndefinedValues<T>>
}

/**
 * @description 过滤掉对象中值为null、undefined和空字符串的属性
 * @param {T extends Record<string, any>} obj 传入的对象数据
 * @returns {Pick<T, FilterEmptyValues<T>>} 返回过滤掉null、undefined和空字符串值的对象
 */
function filterEmptyValues<T extends Record<string, any>>(obj: T): Pick<T, FilterEmptyValues<T>> {
  const result: Partial<T> = {}
  Object.entries(obj).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return
    result[key as keyof T] = value
  })
  return result as Pick<T, FilterEmptyValues<T>>
}

// 传入一个文件格式数组和文件名，判断文件是否符合格式
function checkFileType(fileType: string[], fileName?: string) {
  const regExp = new RegExp(`.(${fileType.join('|')})$`, 'i')
  return regExp.test(fileName ?? '')
}

// 获取图片的尺寸
function getImageSize(url: string) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.src = url
    if (img.complete) {
      resolve(img)
    } else {
      img.onload = () => {
        resolve(img)
      }
      img.onerror = () => {
        reject()
      }
    }
  })
}

/**
 * @description 获取文件后缀名
 * @param {string} filename 文件名
 * @returns {string} 文件后缀名
 */
function getExt(filename: string) {
  const index1 = filename.lastIndexOf('.')
  const index2 = filename.length
  return filename.substring(index1, index2)
}

/**
 * @description 导出数据通用接口
 * @param {T} exportReq
 * @param params
 * @param callback
 */
function downloadFileAjax<T extends (requstData: D) => AxiosPromise, D extends Record<string, any>>(
  exportReq: T,
  params: D,
  callback?: () => void
) {
  exportReq(params)
    .then(({ data, headers }) => {
      const fileName = headers['content-disposition'] ? headers['content-disposition'].split('=')[1] : '未知文件'
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')

      link.style.display = 'none'
      link.href = url
      link.setAttribute('download', decodeURI(fileName))
      document.body.appendChild(link)
      link.click()
      window.URL.revokeObjectURL(link.href)
      document.body.removeChild(link)
      MessagePlugin('success', '导出文件成功！')
      callback?.()
    })
    .catch((_e) => {
      MessagePlugin('error', '导出文件失败！')
      callback?.()
    })
}
/**
 * @description 节流函数
 * @param {T} callback 回调函数
 * @param {number} wait 延迟时间
 * @param {ThrottleOptions} options 配置项
 */
function throttle<T extends (...args: any[]) => any>(
  callback: T,
  wait: number,
  options: ThrottleOptions = {}
): ThrottleCallback<T> {
  let lastTime = 0
  let timeout: NodeJS.Timeout | null = null
  const leading = options.leading !== undefined ? options.leading : true // 是否在开始时执行一次
  const trailing = options.trailing !== undefined ? options.trailing : false // 是否在结束时执行一次

  function exec(time: number, context: ThisParameterType<T>, args: Parameters<T>): void {
    lastTime = time
    timeout = null
    callback.apply(context, args)
  }

  return function (this: ThisParameterType<T>, ...args: Parameters<T>): void {
    // console.log('throttle----', args)
    const currentTime = Date.now()
    if (!lastTime && !leading) {
      lastTime = currentTime
    }
    const remaining = wait - (currentTime - lastTime)
    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
      exec(currentTime, this, args)
    } else if (!timeout && trailing) {
      timeout = setTimeout(() => {
        exec(Date.now(), this, args)
      }, remaining)
    }
  }
}

// 计算百分比
function calculatePercentage(part: number, whole: number): string {
  if (whole === 0) {
    return '0%'
  }
  const percentage = (part / whole) * 100
  return parseFloat(percentage.toFixed(2)) + '%'
}

// 判断两个时间戳间隔是否大于31天
function isIntervalGreaterThan31Days(timestamp1: number, timestamp2: number): boolean {
  // 将时间戳转换为Date对象
  const date1 = new Date(timestamp1 * 1000)
  const date2 = new Date(timestamp2 * 1000)
  // 计算两个日期之间的时间差（以天为单位）
  const diffInDays = Math.floor(((date2 as any) - (date1 as any)) / (1000 * 60 * 60 * 24))
  // 判断时间差是否大于31天
  return diffInDays > 31
}

/**
 * @description 防抖函数
 * @param {T} func 原函数
 * @param {number} wait 延迟时间
 */
function debounce<T extends (...args: any[]) => any>(func: T, wait: number) {
  let timeout: ReturnType<typeof setTimeout> | null = null
  return function (this: Function, ...args: Parameters<T>) {
    const event = new Event('debounce') // 创建一个自定义事件用于触发防抖处理逻辑
    const eventHandler = () => {
      func.apply(this, args) // 执行原函数
      timeout = null // 重置timeout为null，以便下次可以重新计时
    }
    if (!timeout) {
      timeout = setTimeout(eventHandler, wait) // 开始计时，等待达到延迟时间后执行eventHandler函数
    } else {
      clearTimeout(timeout) // 如果已经计时，则清除之前的定时器，重新开始计时
      timeout = setTimeout(eventHandler, wait) // 重新开始计时，等待达到延迟时间后执行eventHandler函数
    }
    window.dispatchEvent(event) // 触发自定义事件，触发防抖处理逻辑
  }
}

/**
 * @description 判断是否是对象
 */
const isObject = (val: unknown): val is Record<string, any> => typeof val === 'object' && val !== null

/**
 * @description 判断是否是布尔值
 */
const isBoolean = (val: unknown): val is boolean => typeof val === 'boolean'

/**
 * 过滤对象中属性值为null或undefined的属性
 * 注意，只做浅层过滤
 */
function omitNullish<T extends Record<string, any>>(obj: T): Required<T> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (typeof value !== 'undefined' && value !== null) {
      acc[key as keyof T] = value
    }
    return acc
  }, {} as Required<T>)
}

export {
  getRandomNum,
  randomString,
  unique,
  urlToBlob,
  downloadUrlFile,
  getFileType,
  getAssetsFile,
  convertToChineseNumeral,
  isEmptyObject,
  objToUrlParams,
  filterUndefinedValues,
  filterEmptyValues,
  checkFileType,
  getImageSize,
  getExt,
  downloadFileAjax,
  throttle,
  calculatePercentage,
  isIntervalGreaterThan31Days,
  debounce,
  deepClone,
  isObject,
  isBoolean,
  omitNullish
}
